import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { PORTAL_UI_NAMESPACE } from '../../i18n/portalUiI18n';
import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { Modal } from '../modal/Modal';

import {
  type ApplicationId,
  buildApplicationUrl,
  detectCurrentApplicationId,
  getVisibleApplications,
  hasAlternativeApplications,
} from './applications';

import styles from './application-switcher.module.scss';

export type ApplicationSwitcherProps = {
  scopes: string[];
  currentAppId?: ApplicationId;
  urls?: Partial<Record<ApplicationId, string>>;
  'data-testid'?: string;
};

export function ApplicationSwitcher({
  scopes,
  currentAppId,
  urls,
  'data-testid': testId = 'application-switcher',
}: ApplicationSwitcherProps) {
  const { t } = useTranslation(PORTAL_UI_NAMESPACE, {
    keyPrefix: 'applicationSwitcher',
  });
  const [opened, setOpened] = useState(false);

  const resolvedCurrentAppId = currentAppId ?? detectCurrentApplicationId();
  const visibleApplications = getVisibleApplications(scopes);

  if (!hasAlternativeApplications(scopes, resolvedCurrentAppId)) {
    return null;
  }

  const triggerLabel = t('triggerLabel');

  return (
    <>
      <Button
        aria-label={triggerLabel}
        className={styles.trigger}
        data-testid={`${testId}-trigger`}
        icon={<GoogleFontIcon name="apps" />}
        onClick={() => setOpened(true)}
        title={triggerLabel}
        variant="secondary"
      />
      <Modal
        modalType="info"
        onClose={() => setOpened(false)}
        opened={opened}
        size={760}
        title={t('title')}
      >
        <div className={styles['modal-content']}>
          <div className={styles.grid} data-testid={`${testId}-grid`}>
            {visibleApplications.map((application) => {
              const isCurrent = application.id === resolvedCurrentAppId;

              return (
                <a
                  key={application.id}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={clsx(
                    styles.card,
                    isCurrent && styles['card--current']
                  )}
                  data-testid={`${testId}-option-${application.id}`}
                  href={
                    urls?.[application.id] ??
                    buildApplicationUrl(application.path)
                  }
                  onClick={(event) => {
                    if (isCurrent) {
                      event.preventDefault();
                    }
                    setOpened(false);
                  }}
                >
                  <span
                    className={clsx(
                      styles['icon-tile'],
                      styles[`icon-tile--${application.iconModifier}`]
                    )}
                  >
                    <GoogleFontIcon name={application.iconName} />
                  </span>
                  <span className={styles['card__header']}>
                    <span className={styles['card__title']}>
                      {t(application.titleKey)}
                    </span>
                    {isCurrent && (
                      <span className={styles['card__badge']}>
                        {t('current')}
                      </span>
                    )}
                  </span>
                  <span className={styles['card__description']}>
                    {t(application.descriptionKey)}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </Modal>
    </>
  );
}
